import { QueryKey, useQuery } from 'react-query';
import { DEDUCTION_TYPE_RECURRING, statutoryComponentNameMap, queryKeys } from '../constants';
import api from 'api';
import { AppError } from 'utils/AppError';
import { CtcBreakdownData, RecurringDeductionsData, SummaryCardData, VpfData } from '../types';
import { paiseAmountToRupee } from 'utils/Numbers';
import {
  FutureCompensation,
  SalaryComponentEarningPayType,
} from 'api/gql-utils/payDetails/compensationTypes';
import {
  PayrollGetEmployeeCompensationDataRequest,
  PayrollGetEmployeeCompensationDataResponse,
  UserFilters,
} from 'api/gql-utils/payDetails/types';

export type FetchCompensationDetailsResponse = {
  summaryCardData: SummaryCardData;
  ctcBreakdownData: CtcBreakdownData;
  futureCompensation: FutureCompensation;
  recurringDeductions: RecurringDeductionsData[];
};

type UseFetchCompensationDetailsProps = {
  userId: string | null;
  payrollMonth: string;
  userFilters?: UserFilters;
  enabled?: boolean;
  onSuccess?: (data: FetchCompensationDetailsResponse) => void;
  onError?: (error: AppError) => void;
  queryKey?: QueryKey;
  userRevisions?: PayrollGetEmployeeCompensationDataRequest['userRevisions'];
};

const useFetchCompensationDetails = ({
  userId,
  payrollMonth,
  userFilters,
  userRevisions,
  enabled = true,
  onSuccess,
  onError = (error: AppError) => {},
  queryKey,
}: UseFetchCompensationDetailsProps) => {
  const { data, isLoading, error, refetch } = useQuery<
    PayrollGetEmployeeCompensationDataResponse,
    AppError,
    FetchCompensationDetailsResponse
  >({
    queryKey: queryKey ?? [queryKeys.FETCH_COMPENSATION_DETAILS, userId, payrollMonth],
    queryFn: () =>
      api.compensationQueries.getEmployeeCompensationData({
        userId,
        payrollMonth,
        userFilters,
        userRevisions,
      }),
    onSuccess,
    enabled,
    select: (
      response: PayrollGetEmployeeCompensationDataResponse,
    ): FetchCompensationDetailsResponse => {
      const payrollData =
        response?.payrollGetCompensationData.data?.payrollData.payrolls?.[0]?.data;
      const futureCompensation = response?.payrollGetCompensationData.data?.futureCompensation;
      const ctcComponent = payrollData?.compensation.ctcComponent;
      const earningComponents = ctcComponent?.earningComponents;
      const monthlyEarningComponents = payrollData?.monthlyCompensationEarnings;
      const statutoryComponents = ctcComponent?.statutoryComponents;
      const deductions = payrollData?.deductions;
      const recurringDeductions =
        deductions
          ?.filter((deduction) => deduction.deductionType === DEDUCTION_TYPE_RECURRING)
          .map((deduction, index) => ({
            id: index.toString(),
            componentId: index.toString(),
            componentName: deduction.name,
            monthlyAmount: paiseAmountToRupee(deduction.amount),
          })) ?? [];

      const totalAmounts = earningComponents
        ? earningComponents.reduce(
            (acc, item) => {
              return { ...acc, [item.payType]: acc[item.payType] + item.amount };
            },
            {
              adhoc: 0,
              fixed: 0,
              variable: 0,
              recurring: 0,
            } as Record<SalaryComponentEarningPayType, number>,
          )
        : {
            adhoc: 0,
            fixed: 0,
            variable: 0,
            recurring: 0,
          };

      const totalRecurringDeductionAmount = deductions
        ? deductions.reduce((acc, item) => {
            if (item.deductionType === 'RECURRING') {
              return acc + item.amount;
            }
            return acc;
          }, 0)
        : 0;
      const nonPayableBenefits =
        earningComponents?.filter((earning) => earning.payType === 'non_payable_benefit') ?? [];

      const totalOtherCtcValue = statutoryComponents
        ? statutoryComponents.reduce((acc, item) => acc + item.amount, 0) +
          (nonPayableBenefits?.reduce((acc, item) => acc + item.amount, 0) ?? 0)
        : 0;

      const percentageTotal = totalAmounts.fixed + totalAmounts.variable + totalOtherCtcValue;
      const fixedPercentage = (totalAmounts.fixed / percentageTotal) * 100;
      const variablePercentage = (totalAmounts.variable / percentageTotal) * 100;
      const otherCtcPercentage = (totalOtherCtcValue / percentageTotal) * 100;

      const summaryCardData: SummaryCardData = {
        annualCtc: paiseAmountToRupee(payrollData?.ctc) ?? 0,
        totalFixedValue: paiseAmountToRupee(totalAmounts.fixed),
        totalVariableValue: paiseAmountToRupee(totalAmounts.variable),
        totalOtherCtcValue: paiseAmountToRupee(totalOtherCtcValue),
        fixedPercentage: fixedPercentage,
        variablePercentage: variablePercentage,
        otherCtcPercentage: otherCtcPercentage,
        totalRecurringDeductionAmount: paiseAmountToRupee(totalRecurringDeductionAmount),
      };

      const fixedDataArray = earningComponents
        ? earningComponents
            .filter((earning) => earning.payType === 'fixed')
            .map((earning, index) => {
              const monthlyComponent = monthlyEarningComponents?.find(
                (monthlyEarning) => monthlyEarning.name === earning.name,
              );

              return {
                id: index.toString(),
                componentName: earning.name,
                perYearAmount: paiseAmountToRupee(earning.amount),
                perMonthAmount: paiseAmountToRupee(monthlyComponent?.amount ?? 0),
              };
            })
        : [];

      const variableDataArray = earningComponents
        ? earningComponents
            .filter((earning) => earning.payType === 'variable')
            .map((earning, index) => ({
              id: index.toString(),
              componentName: earning.name,
              perYearAmount: paiseAmountToRupee(earning.amount),
            }))
        : [];

      const otherCtcDataArray = statutoryComponents
        ? statutoryComponents.map((component, index) => ({
            id: index.toString(),
            componentName:
              statutoryComponentNameMap[component.type as keyof typeof statutoryComponentNameMap],
            perYearAmount: paiseAmountToRupee(component.amount),
          }))
        : [];

      // include Non Payable Benefits in Other CTC Data
      otherCtcDataArray.push(
        ...(earningComponents
          ? nonPayableBenefits.map((earning, index) => ({
              id: index.toString(),
              componentName: earning.name,
              perYearAmount: paiseAmountToRupee(earning.amount),
            }))
          : []),
      );

      const ctcBreakdownData: CtcBreakdownData = {
        fixed: {
          data: fixedDataArray,
          totalMonthlyAmount: fixedDataArray.reduce((acc, item) => acc + item.perMonthAmount, 0),
          totalYearlyAmount: fixedDataArray.reduce((acc, item) => acc + item.perYearAmount, 0),
        },
        variable: {
          data: variableDataArray,
          totalYearlyAmount: variableDataArray.reduce((acc, item) => acc + item.perYearAmount, 0),
        },
        otherCtc: {
          data: otherCtcDataArray,
          totalYearlyAmount: otherCtcDataArray.reduce((acc, item) => acc + item.perYearAmount, 0),
        },
      };

      return { summaryCardData, ctcBreakdownData, futureCompensation, recurringDeductions };
    },
    onError,
  });

  return { data, isLoading, error, refetch };
};

export default useFetchCompensationDetails;
